import { useQuery } from '@tanstack/react-query';
import {
  Roles,
  type InternshipProcessWithNames,
  type OrganizationRegistration,
} from '../../Types/interfaces';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { GetPendingConventionRegistrations } from '../../helper/apiHelper';
import { useMemo } from 'react';
import { useHasRole } from '../useHasRole';

export const usePendingConventions = () => {
  const hasRole = useHasRole();
  const isAuthorized =
    hasRole(Roles.Administrator) || hasRole(Roles.LegalDepartment);

  return useQuery<Array<InternshipProcessWithNames>>({
    queryKey: [...QUERY_KEYS.PENDING_CONVENTIONS],
    queryFn: GetPendingConventionRegistrations,
    enabled: isAuthorized,
  });
};

export const useUnregisteredOrganizations = (
  organizationRegistrations: Array<OrganizationRegistration>,
  stepName: string
) => {
  const registeringStepName = 'RegisteringCollaborationDocument';
  const unsignedRegs = useUnsignedRegistrations(
    organizationRegistrations,
    stepName
  );

  return useMemo(() => {
    const unsignedIds = new Set(unsignedRegs.map((r) => r.id));

    return organizationRegistrations.filter(
      (reg) =>
        reg.processSteps.some(
          (step) =>
            step.stepName === registeringStepName && step.stepStatus === 1
        ) && !unsignedIds.has(reg.id)
    );
  }, [organizationRegistrations, unsignedRegs]);
};

export const useUnsignedRegistrations = (
  organizationRegistrations: Array<OrganizationRegistration>,
  stepName: string
) => {
  return useMemo(() => {
    return organizationRegistrations.filter((reg) =>
      reg.processSteps.some(
        (step) => step.stepName === stepName && step.stepStatus === 1
      )
    );
  }, [organizationRegistrations, stepName]);
};

export const useSignedRegistrations = (
  organizationRegistrations: Array<OrganizationRegistration>,
  stepName: string
) => {
  return useMemo(() => {
    return organizationRegistrations.filter((reg) =>
      reg.processSteps.some(
        (step) => step.stepName === stepName && step.stepStatus > 1
      )
    );
  }, [organizationRegistrations, stepName]);
};
